/**
 * AssignmentManagerModal.jsx - Owner "Manage Assignment" modal
 * Elvis Aloha Collection - Arena Map Editor
 *
 * Opens when the owner clicks an already-assigned seat. Two-step governed
 * lifecycle: Mark Verified (step 1) then Publish Assignment (step 2) are
 * always separate actions. Every transition is server-governed; this modal
 * only ever shows the exact blocker codes the server returns - it never
 * second-guesses the backend's rules.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';

const STATUS_BADGE = {
  pending_review: { label: 'Pending Review', bg: '#FFF7ED', color: '#C2410C', border: '#FDBA74' },
  verified: { label: 'Verified', bg: '#ECFDF5', color: '#047857', border: '#6EE7B7' },
  draft: { label: 'Draft', bg: '#F3F4F6', color: '#4B5563', border: '#D1D5DB' },
  published: { label: 'Published', bg: '#ECFDF5', color: '#047857', border: '#6EE7B7' },
  approved: { label: 'Approved', bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
  calibrated: { label: 'Calibrated', bg: '#F3F4F6', color: '#4B5563', border: '#D1D5DB' },
};

const Badge = ({ status, testId }) => {
  const cfg = STATUS_BADGE[status] || { label: status || '—', bg: '#F3F4F6', color: '#4B5563', border: '#D1D5DB' };
  return (
    <span
      data-testid={testId}
      style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: '999px',
        fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em',
        background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      }}
    >
      {cfg.label}
    </span>
  );
};

const Row = ({ label, value, testId }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '6px 0', borderBottom: '1px solid #F3F4F6' }}>
    <span style={{ fontSize: '12px', color: '#6B7280', fontWeight: 600 }}>{label}</span>
    <span data-testid={testId} style={{ fontSize: '13px', color: '#111827', fontWeight: 500, textAlign: 'right' }}>{value ?? '—'}</span>
  </div>
);

const ActionButton = ({ children, onClick, disabled, variant = 'default', testId, title }) => {
  const styles = {
    default: { bg: 'white', color: '#374151', border: '#D1D5DB' },
    primary: { bg: '#2563EB', color: 'white', border: '#2563EB' },
    success: { bg: '#16A34A', color: 'white', border: '#16A34A' },
    warn: { bg: 'white', color: '#B45309', border: '#FDBA74' },
    danger: { bg: 'white', color: '#DC2626', border: '#FCA5A5' },
  }[variant];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? title : undefined}
      data-testid={testId}
      style={{
        padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
        background: disabled ? '#F3F4F6' : styles.bg,
        color: disabled ? '#9CA3AF' : styles.color,
        border: `1px solid ${disabled ? '#E5E7EB' : styles.border}`,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'opacity 0.15s ease',
      }}
    >
      {children}
    </button>
  );
};

const BLOCKER_LABELS = {
  ASSIGNMENT_NOT_VERIFIED: 'Assignment must be Verified first',
  TICKET_NOT_PUBLIC: 'Ticket is not public',
  TICKET_ARCHIVED: 'Ticket is archived',
  GEOMETRY_NOT_APPROVED: 'Seat geometry is not Approved',
  GEOMETRY_NOT_PUBLISHED: 'Seat geometry is not Published',
  ASSIGNMENT_CONFLICT: 'Another active assignment conflicts',
  TICKET_NOT_FOUND: 'Linked ticket no longer exists',
  GEOMETRY_NOT_FOUND: 'Linked seat geometry no longer exists',
};

export const AssignmentManagerModal = ({
  isOpen,
  seat,
  chartLabel,
  onClose,
  getAssignmentDetail,
  onVerify,
  onReopen,
  onPublish,
  onUnpublish,
  onRemove,
  onReassign,
  getUnassignedTickets,
  onViewFullTicket,
}) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [mode, setMode] = useState('main'); // main | verify-mismatch | reassign
  const [verificationNote, setVerificationNote] = useState('');
  const [mismatchReason, setMismatchReason] = useState('');
  const [reassignSearch, setReassignSearch] = useState('');
  const [reassignCandidates, setReassignCandidates] = useState([]);
  const [reassignTicketId, setReassignTicketId] = useState(null);
  const closeBtnRef = useRef(null);

  const load = useCallback(async () => {
    if (!seat?.assignment_id) return;
    setLoading(true);
    setActionError(null);
    const d = await getAssignmentDetail(seat.assignment_id);
    setDetail(d);
    setLoading(false);
  }, [seat, getAssignmentDetail]);

  useEffect(() => {
    if (isOpen && seat?.assignment_id) {
      setMode('main');
      setVerificationNote('');
      setMismatchReason('');
      load();
    }
  }, [isOpen, seat, load]);

  // Focus trap basics: focus the close button on open, Escape closes.
  useEffect(() => {
    if (!isOpen) return;
    closeBtnRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !seat) return null;

  const assignment = detail?.assignment;
  const ticket = detail?.ticket;
  const geometry = detail?.geometry;
  const mismatch = detail?.location_mismatch;

  const isVerified = assignment?.assignment_status === 'verified';
  const isPublished = assignment?.publication_status === 'published';

  const runAction = async (fn, successMsg) => {
    setBusy(true);
    setActionError(null);
    const res = await fn();
    setBusy(false);
    if (res?.ok) {
      await load();
      return true;
    }
    const detailErr = res?.error;
    let msg = 'Action failed';
    if (typeof detailErr === 'object' && detailErr) {
      if (Array.isArray(detailErr.blockers) && detailErr.blockers.length) {
        msg = detailErr.blockers.map(b => `• ${BLOCKER_LABELS[b.code] || b.message}`).join('\n');
      } else {
        msg = detailErr.message || JSON.stringify(detailErr);
      }
    } else if (typeof detailErr === 'string') {
      msg = detailErr;
    }
    setActionError(msg);
    return false;
  };

  const handleMarkVerified = () => runAction(() => onVerify(assignment.id, { verificationNote }));

  const handleVerifyWithException = async () => {
    if (!mismatchReason.trim()) {
      setActionError('A documented reason is required to verify with a mismatch exception.');
      return;
    }
    const ok = await runAction(() => onVerify(assignment.id, {
      verificationNote, mismatchException: true, mismatchReason,
    }));
    if (ok) setMode('main');
  };

  const handleReopen = () => runAction(() => onReopen(assignment.id, 'Reopened by owner'));
  const handlePublish = () => runAction(() => onPublish(assignment.id));
  const handleUnpublish = () => runAction(() => onUnpublish(assignment.id, 'Unpublished by owner'));

  const handleRemove = async () => {
    const confirmed = window.confirm(
      'This removes the ticket-to-seat relationship only.\n' +
      'The ticket record and seat geometry will remain.\n\n' +
      `Ticket: ${assignment?.ticket_public_reference || assignment?.ticket_id}\n` +
      `Seat: ${seat.section} R${seat.row} S${seat.seat}`
    );
    if (!confirmed) return;
    const ok = await runAction(() => onRemove(assignment.id, 'Removed by owner via Manage Assignment modal'));
    if (ok) onClose?.();
  };

  const openReassign = async () => {
    setMode('reassign');
    setReassignSearch('');
    setReassignTicketId(null);
    const tickets = await getUnassignedTickets(seat.section);
    setReassignCandidates(tickets || []);
  };

  const handleConfirmReassign = async () => {
    if (!reassignTicketId) return;
    const ok = await runAction(() => onReassign(assignment.id, reassignTicketId, 'Reassigned by owner via Manage Assignment modal'));
    if (ok) setMode('main');
  };

  const filteredCandidates = reassignCandidates.filter(t => {
    if (!reassignSearch) return true;
    const q = reassignSearch.toLowerCase();
    return (t.public_reference || '').toLowerCase().includes(q) ||
      (t.registry_reference || '').toLowerCase().includes(q) ||
      String(t.row || '').toLowerCase().includes(q) ||
      String(t.seat || '').toLowerCase().includes(q);
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="assignment-manager-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Manage seat assignment"
    >
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" data-testid="assignment-manager-modal">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Manage Assignment</h2>
            <p className="text-sm text-gray-500 mt-1">
              {chartLabel ? `${chartLabel} · ` : ''}{seat.section} Row {seat.row} Seat {seat.seat}
            </p>
          </div>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
            data-testid="close-assignment-manager-btn"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading && <div style={{ padding: '24px', textAlign: 'center', color: '#6B7280', fontSize: '13px' }}>Loading assignment…</div>}

          {!loading && mode === 'main' && detail && (
            <>
              {/* Status badges */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
                <Badge status={assignment.assignment_status} testId="am-verification-badge" />
                <Badge status={isPublished ? 'published' : 'draft'} testId="am-publication-badge" />
                {geometry && <Badge status={geometry.geometry_status} testId="am-geometry-status-badge" />}
              </div>

              {mismatch && (
                <div
                  data-testid="am-mismatch-warning"
                  style={{
                    marginBottom: '16px', padding: '12px', background: '#FEF2F2', border: '1px solid #FECACA',
                    borderRadius: '8px', fontSize: '12px', color: '#991B1B'
                  }}
                >
                  <strong>Location mismatch:</strong> Ticket location ({ticket?.section} R{ticket?.row} S{ticket?.seat}) does not
                  match seat geometry location ({geometry?.section} R{geometry?.row} S{geometry?.seat}).
                  {assignment.mismatch_exception
                    ? <div style={{ marginTop: '6px' }}>Verified with documented exception: <em>{assignment.mismatch_reason}</em></div>
                    : <div style={{ marginTop: '6px' }}>This must be resolved before verification.</div>}
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <Row label="Ticket Public Reference" value={ticket?.public_reference} testId="am-ticket-public-ref" />
                <Row label="Internal Reference" value={ticket?.internal_reference} testId="am-ticket-internal-ref" />
                <Row label="Ticket Form" value={ticket?.ticket_form} testId="am-ticket-form" />
                <Row label="Ticket Section / Row / Seat" value={ticket ? `${ticket.section || '—'} / ${ticket.row || '—'} / ${ticket.seat || '—'}` : '—'} testId="am-ticket-location" />
                <Row label="Collection" value={ticket?.public_collection_label} testId="am-collection-label" />
                <Row label="Grade" value={ticket?.grading_summary || '—'} testId="am-grade" />
                <Row label="Ticket Public Status" value={ticket?.is_public ? 'Public' : 'Private'} testId="am-ticket-public-status" />
                <Row label="Geometry ID" value={geometry?.id} testId="am-geometry-id" />
                <Row label="Verification Note" value={assignment.verification_note || '—'} testId="am-verification-note" />
              </div>

              {actionError && (
                <div data-testid="am-action-error" style={{
                  marginBottom: '12px', padding: '10px', background: '#FEF2F2', border: '1px solid #FECACA',
                  borderRadius: '6px', fontSize: '12px', color: '#B91C1C', whiteSpace: 'pre-wrap'
                }}>
                  {actionError}
                </div>
              )}

              {!isVerified && (
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: '4px' }}>
                    Verification note (optional)
                  </label>
                  <textarea
                    value={verificationNote}
                    onChange={(e) => setVerificationNote(e.target.value)}
                    placeholder="Optional documentation - not required to verify"
                    rows={2}
                    data-testid="am-verification-note-input"
                    style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px' }}
                  />
                </div>
              )}
            </>
          )}

          {!loading && mode === 'verify-mismatch' && (
            <div>
              <p style={{ fontSize: '13px', color: '#374151', marginBottom: '12px' }}>
                Ticket and seat geometry locations differ. Choose how to proceed:
              </p>
              <label style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', display: 'block', marginBottom: '4px' }}>
                Documented exception reason (required)
              </label>
              <textarea
                value={mismatchReason}
                onChange={(e) => setMismatchReason(e.target.value)}
                rows={3}
                data-testid="am-mismatch-reason-input"
                style={{ width: '100%', padding: '8px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px' }}
              />
              {actionError && <div style={{ marginTop: '8px', color: '#B91C1C', fontSize: '12px' }}>{actionError}</div>}
            </div>
          )}

          {!loading && mode === 'reassign' && (
            <div>
              <input
                type="text"
                placeholder="Search by reference, row, or seat…"
                value={reassignSearch}
                onChange={(e) => setReassignSearch(e.target.value)}
                data-testid="am-reassign-search-input"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: '6px', fontSize: '13px', marginBottom: '10px' }}
              />
              <div style={{ maxHeight: '260px', overflowY: 'auto' }}>
                {filteredCandidates.length === 0 && (
                  <div style={{ fontSize: '12px', color: '#6B7280', padding: '12px 0' }}>No eligible unassigned tickets found.</div>
                )}
                {filteredCandidates.map(t => (
                  <div
                    key={t.id}
                    onClick={() => setReassignTicketId(t.id)}
                    data-testid={`am-reassign-candidate-${t.id}`}
                    style={{
                      padding: '10px', borderRadius: '6px', cursor: 'pointer', marginBottom: '6px',
                      border: reassignTicketId === t.id ? '2px solid #2563EB' : '1px solid #E5E7EB',
                      background: reassignTicketId === t.id ? '#EFF6FF' : 'white',
                    }}
                  >
                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{t.public_reference || t.registry_reference || t.id}</div>
                    <div style={{ fontSize: '11px', color: '#6B7280' }}>Row {t.row} · Seat {t.seat}</div>
                  </div>
                ))}
              </div>
              {actionError && <div style={{ marginTop: '8px', color: '#B91C1C', fontSize: '12px' }}>{actionError}</div>}
            </div>
          )}
        </div>

        {/* Footer - state-aware actions */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex-shrink-0">
          {mode === 'main' && detail && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <ActionButton testId="am-view-full-ticket-btn" onClick={() => onViewFullTicket?.(ticket)} disabled={!ticket}>
                  View Full Ticket
                </ActionButton>
                <ActionButton testId="am-reassign-btn" onClick={openReassign} disabled={busy}>
                  Reassign Ticket
                </ActionButton>
                <ActionButton testId="am-remove-btn" variant="danger" onClick={handleRemove} disabled={busy}>
                  Remove Assignment
                </ActionButton>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {!isVerified && (
                  <ActionButton
                    testId="am-mark-verified-btn"
                    variant="success"
                    disabled={busy}
                    onClick={() => (mismatch ? setMode('verify-mismatch') : handleMarkVerified())}
                  >
                    Mark Verified
                  </ActionButton>
                )}
                {isVerified && (
                  <ActionButton testId="am-reopen-btn" variant="warn" onClick={handleReopen} disabled={busy}>
                    Reopen Verification
                  </ActionButton>
                )}
                {isVerified && !isPublished && (
                  <ActionButton testId="am-publish-btn" variant="primary" onClick={handlePublish} disabled={busy}>
                    Publish Assignment
                  </ActionButton>
                )}
                {isPublished && (
                  <ActionButton testId="am-unpublish-btn" onClick={handleUnpublish} disabled={busy}>
                    Unpublish Assignment
                  </ActionButton>
                )}
              </div>
            </div>
          )}

          {mode === 'verify-mismatch' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <ActionButton testId="am-mismatch-cancel-btn" onClick={() => setMode('main')} disabled={busy}>Cancel</ActionButton>
              <ActionButton testId="am-mismatch-correct-ticket-btn" onClick={() => onViewFullTicket?.(ticket)} disabled={busy}>Correct Ticket</ActionButton>
              <ActionButton testId="am-mismatch-verify-exception-btn" variant="success" onClick={handleVerifyWithException} disabled={busy}>
                Verify with Documented Exception
              </ActionButton>
            </div>
          )}

          {mode === 'reassign' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <ActionButton testId="am-reassign-cancel-btn" onClick={() => setMode('main')} disabled={busy}>Cancel</ActionButton>
              <ActionButton
                testId="am-reassign-confirm-btn"
                variant="primary"
                onClick={handleConfirmReassign}
                disabled={busy || !reassignTicketId}
              >
                Confirm Reassignment
              </ActionButton>
            </div>
          )}

          {mode === 'main' && (
            <div style={{ marginTop: '10px', textAlign: 'right' }}>
              <button onClick={onClose} data-testid="am-close-footer-btn" style={{ fontSize: '13px', color: '#6B7280', background: 'none', border: 'none', cursor: 'pointer' }}>
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentManagerModal;
